import { Component } from "@theme/component";
import {
  fetchConfig,
  preloadImage,
  onAnimationEnd,
  yieldToMainThread,
} from "@theme/utilities";
import {
  ThemeEvents,
  CartAddEvent,
  CartErrorEvent,
  CartUpdateEvent,
  VariantUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";
import { morph } from "@theme/morph";

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;
const VISIBLE_SUCCESS_MESSAGE_DISPLAY_DURATION = 2500;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ["addToCartButton"];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("pointerenter", this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(
        /** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId),
      );
    }
    this.removeEventListener("pointerenter", this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest("form");
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (
      this.closest("product-form-component")
    );
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== "true") {
      const isDefaultProductInCart =
        productForm?.isDefaultVariantAlreadyInCart?.() === true;

      if (!isDefaultProductInCart) {
        const animationEnabled = this.dataset.addToCartAnimation === "true";
        if (animationEnabled && !event.target.closest(".quick-add-modal")) {
          this.#animateFlyToCart();
        }
        this.animateAddToCart();
      }
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector(".header-actions__cart-icon");

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (
      document.createElement("fly-to-cart")
    );

    let flyToCartClass = addToCartButton.classList.contains("quick-add__button")
      ? "fly-to-cart--quick"
      : "fly-to-cart--main";

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty("background-image", `url(${image})`);
    flyToCartElement.style.setProperty("--start-opacity", "0");
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(
      /** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId),
    );
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== "true") {
      addToCartButton.dataset.added = "true";
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute("data-added");

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get("add-to-cart-component")) {
  customElements.define("add-to-cart-component", AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ["variantId", "liveRegion"];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {number | undefined} */
  #successFeedbackTimeout;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];

  /** @type {number} */
  #currentVariantCartQuantity = 0;

  /** @type {Promise<Cart | null> | null} */
  #defaultCartSyncPromise = null;

  /** @type {boolean} */
  #defaultCartStateReady = false;

  /** Prevent overlapping add-to-cart requests for this form. */
  #addToCartInFlight = false;

  /** Prevent double clicks while waiting for a cart lookup. */
  #defaultSubmitAwaitingCart = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section, dialog, product-card");
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal,
    });
    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#onVariantSelected,
      { signal },
    );

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, {
      signal,
    });

    if (this.matches("[data-default-product-form]")) {
      this.#defaultCartSyncPromise = this.#syncDefaultAddToCartStateFromCart();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#successFeedbackTimeout)
      clearTimeout(this.#successFeedbackTimeout);
    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    );
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find(
      (item) => item.variant_id.toString() === variantIdInput.value.toString(),
    );
    const cartQty = cartItem ? Number(cartItem.quantity) || 0 : 0;
    this.#currentVariantCartQuantity = cartQty;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (
      this.querySelector("quantity-selector-component")
    );
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);
    this.#updateDefaultAddToCartLabel(cartQty);
    if (cartQty > 0) this.#clearAddToCartError();

    return cartQty;
  }

  /**
   * Updates the direct Default product button label with the current cart quantity.
   * @param {number} cartQty - The quantity in cart
   */
  #updateDefaultAddToCartLabel(cartQty) {
    if (!this.matches("[data-default-product-form]")) return;

    const button = /** @type {HTMLButtonElement | null} */ (
      this.querySelector("[data-default-add-to-cart-button]")
    );
    const label = this.querySelector("[data-default-add-to-cart-label]");
    if (!button || !label) return;

    if (!button.dataset.originalAddToCartText) {
      button.dataset.originalAddToCartText =
        label.textContent?.trim() || "ADD TO CART";
    }

    label.textContent =
      cartQty > 0
        ? `${cartQty} IN CART`
        : button.dataset.originalAddToCartText || "ADD TO CART";
  }

  /**
   * A visible response to the first click while the cart request is pending.
   * Keeps the button enabled so #processAddToCart can validate availability;
   * repeated submits are blocked by the existing in-flight guards.
   * @param {boolean} pending
   */
  #setDefaultAddPending(pending) {
    if (!this.matches("[data-default-product-form]")) return;
    const button = this.querySelector("[data-default-add-to-cart-button]");
    const label = this.querySelector("[data-default-add-to-cart-label]");
    if (!button || !label) return;

    if (pending) {
      if (!button.dataset.originalAddToCartText) {
        button.dataset.originalAddToCartText = label.textContent?.trim() || "ADD TO CART";
      }
      button.setAttribute("aria-busy", "true");
      label.textContent = "ADDING...";
    } else {
      button.removeAttribute("aria-busy");
      this.#updateDefaultAddToCartLabel(this.#currentVariantCartQuantity);
    }
  }

  /**
   * Converts Shopify AJAX cart errors into safe customer-facing text.
   * @param {*} error
   * @returns {string}
   */
  #getCartErrorMessage(error) {
    const fallback = "Unable to add this item to cart.";

    const rawMessage =
      typeof error === "string"
        ? error
        : error instanceof Error
          ? error.message
          : error?.description || error?.message || error?.errors || fallback;

    let message = rawMessage;

    if (typeof message !== "string") {
      if (Array.isArray(message)) {
        message = message.filter(Boolean).join(" ");
      } else if (message && typeof message === "object") {
        message = Object.values(message).flat().filter(Boolean).join(" ");
      } else {
        message = fallback;
      }
    }

    message = message.trim();

    if (!message || message === "[object Object]") {
      return fallback;
    }

    try {
      const parsed = JSON.parse(message);
      return this.#getCartErrorMessage(parsed);
    } catch (_error) {
      // Message is already plain text.
    }

    const normalizedMessage = message.toLowerCase();
    if (
      normalizedMessage.includes("maximum quantity") ||
      normalizedMessage.includes("already in your cart")
    ) {
      return "This piece is already in your cart. Only 1 is currently available.";
    }

    return message;
  }

  /**
   * Shows a safe add-to-cart error message in the product form error area.
   * @param {*} error
   * @returns {string}
   */
  #displayAddToCartError(error) {
    const errorMessage = this.#getCartErrorMessage(error);
    const { addToCartTextError } = this.refs;

    if (addToCartTextError) {
      addToCartTextError.textContent = errorMessage;
      addToCartTextError.setAttribute("aria-live", "polite");
      addToCartTextError.classList.remove("hidden");
    }

    this.#setLiveRegionText(errorMessage);

    if (this.#timeout) clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => {
      if (addToCartTextError) addToCartTextError.classList.add("hidden");
      this.#clearLiveRegionText();
    }, ERROR_MESSAGE_DISPLAY_DURATION);

    return errorMessage;
  }

  /**
   * Clears any visible add-to-cart error after Shopify confirms a successful add.
   */
  #clearAddToCartError() {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);
    if (addToCartTextError) {
      addToCartTextError.textContent = "";
      addToCartTextError.classList.add("hidden");
      addToCartTextError.setAttribute("aria-live", "polite");
    }
    this.#clearLiveRegionText();
  }

  /**
   * Dispatches cart synchronization while keeping UI listener failures separate
   * from the completed Shopify add request.
   * @param {Cart | null | undefined} cart
   * @param {string} sourceId
   * @param {Record<string, any>} data
   */
  #dispatchCartAddSafely(cart, sourceId, data) {
    try {
      // An unsuccessful /cart.js read must not reset the visible cart badge
      // to zero. Omit itemCount so cart-icon can retry its own refresh.
      const authoritativeItemCount = cart?.item_count ?? data.itemCount;
      this.dispatchEvent(
        new CartAddEvent(cart ?? undefined, sourceId, {
          ...data,
          ...(authoritativeItemCount == null
            ? {}
            : { itemCount: Number(authoritativeItemCount) }),
        }),
      );
    } catch (error) {
      console.error(
        "Cart UI synchronization failed after successful add:",
        error,
      );
    }
  }

  /**
   * Reads Shopify's AJAX response based on its actual contents, not its MIME type.
   * Shopify may return valid JSON labelled application/javascript.
   * @param {Response} response
   * @returns {Promise<any>}
   */
  async #parseJsonResponse(response) {
    const responseText = await response.text();

    try {
      const data = JSON.parse(responseText);
      // Preserve normal Shopify validation-error handling even when an HTTP
      // error response does not include its own status field.
      if (!response.ok && !data.status) {
        return { ...data, status: response.status };
      }
      return data;
    } catch (error) {
      if (error instanceof SyntaxError) {
        throw new Error(
          `Unexpected cart response (${response.status}). Check the cart before trying again.`,
        );
      }
      throw error;
    }
  }

  /**
   * Fetches the current cart and updates form quantity state.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    );
    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch("/cart.js", { cache: "no-store" });
      if (!response.ok) throw new Error("Unable to read cart state");
      const cart = await response.json();

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error("Failed to fetch cart quantity:", error);
      return null;
    }
  }

  /**
   * Returns true when authoritative cart state says this default variant is already in cart.
   * @returns {boolean}
   */
  isDefaultVariantAlreadyInCart() {
    return (
      this.matches("[data-default-product-form]") &&
      this.#currentVariantCartQuantity > 0
    );
  }

  /**
   * Applies pending/ready affordances around the initial default-form cart lookup.
   * @param {boolean} isPending
   */
  #setDefaultCartStatePending(isPending) {
    if (!this.matches("[data-default-product-form]")) return;
    const button = this.querySelector("[data-default-add-to-cart-button]");
    // Keep first-click submit active; only the product's real availability
    // may disable it. handleSubmit awaits the cart promise without disabling.
    button?.toggleAttribute("data-cart-sync-pending", isPending);
  }

  /**
   * Returns the quantity for a variant from authoritative cart data.
   * @param {Cart | null | undefined} cart
   * @param {FormDataEntryValue | string | number | null | undefined} variantId
   * @returns {number}
   */
  #getCartQuantityForVariant(cart, variantId) {
    if (!cart?.items || variantId == null) return 0;
    const cartItem = cart.items.find(
      (item) => item.variant_id.toString() === variantId.toString(),
    );
    return cartItem ? Number(cartItem.quantity) || 0 : 0;
  }

  /**
   * Sends the customer to the cart page.
   */
  #navigateToCart() {
    window.location.href = Theme.routes.cart_url || "/cart";
  }

  /**
   * Deterministically syncs the Default product add-to-cart label from /cart.js.
   * @returns {Promise<Cart | null>} The authoritative cart, or null if unavailable.
   */
  async #syncDefaultAddToCartStateFromCart() {
    if (!this.matches("[data-default-product-form]")) return null;

    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    );
    if (!variantIdInput?.value) {
      this.#currentVariantCartQuantity = 0;
      this.#updateDefaultAddToCartLabel(0);
      this.#defaultCartStateReady = true;
      return null;
    }

    this.#defaultCartStateReady = false;
    this.#setDefaultCartStatePending(true);

    try {
      const response = await fetch("/cart.js", { cache: "no-store" });
      if (!response.ok) throw new Error("Unable to read cart state");
      const cart = /** @type {Cart} */ (await response.json());
      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error("Failed to sync default add-to-cart state:", error);
      return null;
    } finally {
      this.#defaultCartStateReady = true;
      this.#setDefaultCartStatePending(false);
      this.#defaultCartSyncPromise = null;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    const isDefaultProductForm = this.matches("[data-default-product-form]");

    // Non-default product forms skip their own cart events; Default forms must still
    // sync the visible button label from the cart quantity.
    if (
      !isDefaultProductForm &&
      (event.detail?.sourceId === this.id ||
        event.detail?.data?.source === "product-form-component")
    )
      return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else if (isDefaultProductForm) {
      await this.#syncDefaultAddToCartStateFromCart();
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  async handleSubmit(event) {
    event.preventDefault();

    if (this.#addToCartInFlight || this.#defaultSubmitAwaitingCart) return;

    if (this.#variantChangeInProgress) {
      const quantity = this.#getQuantity();

      if (this.matches("[data-default-product-form]")) {
        // Wait for the authoritative variant update. The URL and hidden input
        // can still refer to the previous combination at click time.
        // One pending click must never enqueue multiple items.
        this.#addToCartQueue = [{ variantId: "", quantity }];
        this.#defaultSubmitAwaitingCart = true;
        this.#setDefaultAddPending(true);
        return;
      }

      const intendedVariantId = this.#getIntendedVariantId();
      if (intendedVariantId) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    if (this.matches("[data-default-product-form]")) {
      // The cart is already fetched once when this form connects, and cart:update
      // keeps the variant quantity current. Do not perform a fresh /cart.js
      // request on every click: it delays the POST and feels like a dead button.
      this.#defaultSubmitAwaitingCart = true;
      this.#setDefaultAddPending(true);
      let requestStarted = false;
      try {
        // Only wait if the one-time initial cart lookup is still in progress.
        // It protects against adding another copy of an existing cart item.
        if (this.#defaultCartSyncPromise) {
          await this.#defaultCartSyncPromise;
        }

        if (this.#currentVariantCartQuantity > 0) {
          this.#clearAddToCartError();
          this.#navigateToCart();
          return;
        }

        this.#processAddToCart(undefined, undefined, event);
        requestStarted = this.#addToCartInFlight;
      } finally {
        this.#defaultSubmitAwaitingCart = false;
        if (!requestStarted) this.#setDefaultAddPending(false);
      }
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return (
      new URL(window.location.href).searchParams.get("variant") ||
      this.refs.variantId?.value ||
      undefined
    );
  }

  /** @returns {number} */
  #getQuantity() {
    return (
      Number(this.refs.quantitySelector?.getValue?.()) ||
      Number(this.dataset.quantityDefault) ||
      1
    );
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    if (this.#addToCartInFlight) return;
    if (this.#timeout) clearTimeout(this.#timeout);
    if (this.#successFeedbackTimeout)
      clearTimeout(this.#successFeedbackTimeout);

    const allAddToCartContainers =
      /** @type {NodeListOf<AddToCartComponent>} */ (
        this.querySelectorAll("add-to-cart-component")
      );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled,
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector("form");
    if (!form) throw new Error("Product form element missing");

    if (
      !overrideVariantId &&
      this.matches("[data-default-product-form]") &&
      this.#currentVariantCartQuantity > 0
    ) {
      this.#clearAddToCartError();
      this.#navigateToCart();
      return;
    }

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || "";
        const errorMessage = errorTemplate.replace(
          "{{ maximum }}",
          validation.maxQuantity?.toString() || "",
        );
        this.#displayAddToCartError(errorMessage);

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set("id", overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set("quantity", overrideQuantity.toString());
    }

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component",
    );
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append("sections", cartItemComponentsSectionIds.join(","));
    });

    const fetchCfg = fetchConfig("javascript", { body: formData });

    this.#addToCartInFlight = true;
    fetch(Theme.routes.cart_add_url || "/cart/add.js", {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: "application/json",
      },
    })
      .then((response) => this.#parseJsonResponse(response))
      .then(async (response) => {
        if (response.status) {
          const id = formData.get("id");
          const isDefaultProductForm = this.matches(
            "[data-default-product-form]",
          );
          const cart = isDefaultProductForm
            ? await this.#fetchAndUpdateCartQuantity()
            : null;
          const currentQuantity = this.#getCartQuantityForVariant(cart, id);

          if (isDefaultProductForm && currentQuantity > 0) {
            this.#currentVariantCartQuantity = currentQuantity;
            this.#clearAddToCartError();
            this.#updateDefaultAddToCartLabel(currentQuantity);
            this.dispatchEvent(
              new CartUpdateEvent(cart ?? undefined, this.id, {
                source: "product-form-component",
                productId: this.dataset.productId,
              }),
            );
            return;
          }

          this.dispatchEvent(
            new CartErrorEvent(
              form.getAttribute("id") || "",
              response.message,
              response.description,
              response.errors,
            ),
          );

          this.#displayAddToCartError(response);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: "product-form-component",
              itemCount:
                Number(formData.get("quantity")) ||
                Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            }),
          );

          return;
        } else {
          try {
            const id = formData.get("id");

            this.#clearAddToCartError();

            if (!id) throw new Error("Form ID is required");

            // Add aria-live region to inform screen readers that the item was added
            // Get the added text from any add-to-cart button
            const anyAddToCartButton =
              allAddToCartContainers[0]?.refs.addToCartButton;
            if (anyAddToCartButton) {
              const addedTextElement = anyAddToCartButton.querySelector(
                ".add-to-cart-text--added",
              );
              const addedText =
                addedTextElement?.textContent?.trim() ||
                Theme.translations.added;

              this.#setLiveRegionText(addedText);

              setTimeout(() => {
                this.#clearLiveRegionText();
              }, SUCCESS_MESSAGE_DISPLAY_DURATION);
            }

            // Shopify has accepted the add. Show success immediately instead
            // of waiting for a second /cart.js request before giving feedback.
            // The authoritative cart read below still reconciles the final count.
            const confirmedAddQty = Number(response.quantity) ||
              Number(formData.get("quantity")) ||
              Number(this.dataset.quantityDefault) ||
              1;
            if (this.matches("[data-default-product-form]")) {
              this.#currentVariantCartQuantity = confirmedAddQty;
              this.#showAddToCartSuccess(allAddToCartContainers, confirmedAddQty);
            }

            // Fetch the updated cart to get the actual total quantity for this variant
            const cart = await this.#fetchAndUpdateCartQuantity();
            const variantCartQty =
              cart?.items?.find(
                (item) => item.variant_id.toString() === id.toString(),
              )?.quantity ||
              Number(formData.get("quantity")) ||
              Number(this.dataset.quantityDefault);
            this.#showAddToCartSuccess(allAddToCartContainers, variantCartQty);

            this.#dispatchCartAddSafely(cart, id.toString(), {
              source: "product-form-component",
              productId: this.dataset.productId,
              sections: response.sections,
            });
          } catch (error) {
            console.error(
              "Cart UI synchronization failed after successful add:",
              error,
            );
          }
        }
      })
      .catch(async (error) => {
        console.error("Unable to confirm add-to-cart response:", error);

        // Shopify may have accepted the item even if the response could not
        // be decoded. Never repeat the POST. Reconcile from the actual cart.
        const cart = await this.#fetchAndUpdateCartQuantity();
        if (cart?.items) {
          const id = formData.get("id");
          const variantCartQty = this.#getCartQuantityForVariant(cart, id);
          this.#dispatchCartAddSafely(cart, id?.toString() || this.id, {
            source: "product-form-component",
            productId: this.dataset.productId,
          });

          // The selected variant is genuinely in Shopify's cart. Do not show
          // the misleading "couldn't confirm" error in this case.
          if (variantCartQty > 0) {
            this.#clearAddToCartError();
            this.#showAddToCartSuccess(allAddToCartContainers, variantCartQty);
            return;
          }
        }

        this.#displayAddToCartError(
          "We couldn't confirm this addition. Please check your cart before trying again.",
        );
      })
      .finally(() => {
        this.#addToCartInFlight = false;
        this.#setDefaultAddPending(false);
        if (event) {
          cartPerformance.measureFromEvent("add:user-action", event);
        }
      });
  }

  /** @param {Array<{variantId: string, quantity: number}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0 || this.#addToCartInFlight) return;

    if (this.#timeout) clearTimeout(this.#timeout);
    if (this.#successFeedbackTimeout)
      clearTimeout(this.#successFeedbackTimeout);

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component",
    );
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const payload = {
      items: items.map((item) => ({
        id: Number(item.variantId),
        quantity: item.quantity,
      })),
      sections: cartItemComponentsSectionIds.join(","),
    };

    this.#addToCartInFlight = true;
    fetch(Theme.routes.cart_add_url || "/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    })
      .then((response) => this.#parseJsonResponse(response))
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(
              this.id,
              response.message,
              response.description,
              response.errors,
            ),
          );

          this.#displayAddToCartError(response);

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: "product-form-component",
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
            }),
          );
          return;
        }

        try {
          this.#clearAddToCartError();

          const allAddToCartContainers =
            /** @type {NodeListOf<AddToCartComponent>} */ (
              this.querySelectorAll("add-to-cart-component")
            );
          const anyAddToCartButton =
            allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector(
              ".add-to-cart-text--added",
            );
            const addedText =
              addedTextElement?.textContent?.trim() || Theme.translations.added;
            this.#setLiveRegionText(addedText);
            setTimeout(
              () => this.#clearLiveRegionText(),
              SUCCESS_MESSAGE_DISPLAY_DURATION,
            );
          }

          const cart = await this.#fetchAndUpdateCartQuantity();
          this.#showAddToCartSuccess(
            allAddToCartContainers,
            items.reduce((sum, item) => sum + item.quantity, 0),
          );

          const totalQuantity = items.reduce(
            (sum, item) => sum + item.quantity,
            0,
          );
          this.#dispatchCartAddSafely(cart, this.id, {
            source: "product-form-component",
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
          });
        } catch (error) {
          console.error(
            "Cart UI synchronization failed after successful add:",
            error,
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#addToCartInFlight = false;
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle("hidden", cartQty === 0);
    }
  }

  /**
   * Updates add-to-cart success feedback for the active product form.
   * @param {NodeListOf<AddToCartComponent>} addToCartContainers
   * @param {number} cartQty
   */
  #showAddToCartSuccess(addToCartContainers, cartQty) {
    if (this.matches("[data-default-product-form]")) {
      this.#updateDefaultAddToCartLabel(cartQty);
      return;
    }

    for (const container of addToCartContainers) {
      const button = container.refs.addToCartButton;
      const text = button?.querySelector("[data-add-to-cart-label]");
      if (!button || !text) continue;

      if (!button.dataset.originalAddToCartText) {
        button.dataset.originalAddToCartText = text.textContent?.trim() || "";
      }
      text.textContent = Theme.translations.added || "Added to cart";
    }

    this.#successFeedbackTimeout = setTimeout(() => {
      for (const container of addToCartContainers) {
        const button = container.refs.addToCartButton;
        const text = button?.querySelector("[data-add-to-cart-label]");
        const original = button?.dataset.originalAddToCartText;
        if (text && original) text.textContent = original;
      }
    }, VISIBLE_SUCCESS_MESSAGE_DISPLAY_DURATION);
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = "";
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(
    currentElement,
    newElement,
    insertReferenceElement = null,
  ) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement(
        "beforebegin",
        /** @type {Element} */ (newElement.cloneNode(true)),
      );
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? "";

    this.#variantChangeInProgress = false;

    // Default forms resolve the selected variant and its cart quantity before
    // adding a single item. Keep batch behaviour for other product forms.
    if (this.#addToCartQueue.length > 0 && !this.matches("[data-default-product-form]")) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    }

    const {
      addToCartButtonContainer: currentAddToCartButtonContainer,
      acceleratedCheckoutButtonContainer,
    } = this.refs;
    const currentAddToCartButton =
      currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (
      !currentAddToCartButtonContainer ||
      (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)
    )
      return;

    // Update the button state
    if (
      event.detail.resource == null ||
      event.detail.resource.available == false
    ) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector(
      'product-form-component [ref="addToCartButton"]',
    );
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
      // The initial button label is cached for cart-count feedback.
      // Refresh it on variant change (Add to cart ↔ Preorder now).
      if (this.matches("[data-default-product-form]")) {
        const nextLabel = newAddToCartButton.querySelector("[data-default-add-to-cart-label]")?.textContent?.trim();
        if (nextLabel) {
          currentAddToCartButton.dataset.originalAddToCartText = nextLabel;
        }
      }
    }

    if (acceleratedCheckoutButtonContainer) {
      if (
        event.detail.resource == null ||
        event.detail.resource.available == false
      ) {
        acceleratedCheckoutButtonContainer?.setAttribute("hidden", "true");
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute("hidden");
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia =
        event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          "data-product-variant-media",
          productVariantMedia + "&width=100",
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector(
        'quantity-selector-component input[ref="quantityInput"]',
      )
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step,
      );
    }

    const newQuantityRules =
      event.detail.data.html.querySelector(".quantity-rules");
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem =
      event.detail.data.html.querySelector("price-per-item");
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if (
      (isQuantityRulesChanging || isPricePerItemChanging) &&
      quantitySelector
    ) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector(
        ".product-form-buttons",
      );

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector(
            'quantity-selector-component input[ref="quantityInput"]',
          )
        );

        if (
          this.refs.quantitySelector?.updateConstraints &&
          newQuantityInputElement &&
          currentQuantityValue
        ) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step,
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        [".quantity-label", quantityLabel, quantitySelector],
        [".quantity-rules", quantityRules, this.refs.productFormButtons],
        ["price-per-item", pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(
          currentElement,
          event.detail.data.html.querySelector(selector),
          fallback,
        );
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing =
      event.detail.data.html.querySelector("volume-pricing");
    this.#morphOrUpdateElement(
      currentVolumePricing,
      newVolumePricing,
      this.refs.productFormButtons,
    );

    const hasB2BFeatures =
      quantityRules ||
      newQuantityRules ||
      pricePerItem ||
      newPricePerItem ||
      currentVolumePricing ||
      newVolumePricing;

    if (this.matches("[data-default-product-form]")) {
      await this.#syncDefaultAddToCartStateFromCart();

      // A more recent option change supersedes this response. Leave the
      // queued click for the final variant, not an outdated combination.
      if (this.#variantChangeInProgress || this.#addToCartQueue.length === 0) return;

      const [{ quantity }] = this.#addToCartQueue.splice(0, 1);
      this.#defaultSubmitAwaitingCart = false;

      if (!event.detail.resource?.id || event.detail.resource.available === false) {
        this.#setDefaultAddPending(false);
        this.#displayAddToCartError("Please select an available option combination.");
        return;
      }

      if (this.#currentVariantCartQuantity > 0) {
        this.#clearAddToCartError();
        this.#setDefaultAddPending(false);
        this.#navigateToCart();
        return;
      }

      this.#setDefaultAddPending(true);
      this.#processAddToCart(undefined, quantity);
      if (!this.#addToCartInFlight) this.#setDefaultAddPending(false);
      return;
    }

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
  };
}

if (!customElements.get("product-form-component")) {
  customElements.define("product-form-component", ProductFormComponent);
}
